import { useEffect, useState } from "react";
import { sortBy } from "lodash";
import api from "../api";
import { loadStripe } from "@stripe/stripe-js";
import { useRecoilValue } from "recoil";
import { billingAtom } from "../store/billingAtom";
import useFetchPlans from "./useFetchPlans";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const usePlans = ({ successUrl, onCompleted }) => {
  const billingInfo = useRecoilValue(billingAtom);
  const [frequency, setFrequency] = useState("monthly");
  const { paymentPlans } = useFetchPlans();
  const [loading, setLoading] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showChangePlanModal, setShowChangePlanModal] = useState(false);
  const [showDowngradePlanModal, setShowDowngradePlanModal] = useState(false);
  const [showCancelPlanModal, setShowCancelPlanModal] = useState(false);
  const [showRenewPlanModal, setShowRenewPlanModal] = useState(false);

  const active_plan = billingInfo?.active_plan?.payment_plan
  const next_plan = billingInfo?.next_plan?.payment_plan

  const getAction = () => {
    if (showDowngradePlanModal) return "downgrade";
    if (showCancelPlanModal) return "cancel";
    if (showRenewPlanModal) return "renew";
    return "upgrade";
  };

  const action = getAction();

  const plans = sortBy(
    paymentPlans.filter(i => i.type === frequency),
    "cost"
  );

  const onChangePlanSubmit = async (plan = selectedPlan) => {
    try {
      if (loading) return null;

      setLoading(plan.id);

      const url = new URL(successUrl, window.location.origin);

      url.searchParams.set("action", action);

      url.searchParams.set("status", "success");

      const success_url = url.toString();

      const to_default = active_plan?.id === plan.id;

      // renew plan logic
      if (next_plan?.id && plan.id === active_plan?.id && billingInfo?.active_plan?.canceled) {
        const { data } = await api.post("api/billing/renew-plan");

        console.log("renew response", data);

        window.location.href = success_url;
        
        return;
      }

      const { data } = await api.post("api/billing/billing_plan", {
        payment_plan_id: to_default ? null : plan.id,
        to_default,
        success_url,
        cancel_url: window.location.href,
      });

      const { session_id, subscription_id } = data;

      if (onCompleted) onCompleted(data);

      if (subscription_id) {
        window.location.href = success_url;
        return;
      }

      if (session_id) {
        const stripe = await stripePromise;

        await stripe.redirectToCheckout({
          sessionId: session_id,
        });

        await stripe.redirectToCheckout({
          sessionId: session_id,
        });

        return;
      }

      alert("Something went wrong. Please try again later.");
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(null);
    }
  };

  const onChangePlanIntent = plan => {
    if (loading) return null;

    const isCurrentPlan = active_plan?.id === plan?.id;

    if (!active_plan || +active_plan.cost === 0) {
      onChangePlanSubmit(plan);
      return;
    }

    if (next_plan?.id && plan.id === active_plan?.id && billingInfo?.active_plan?.canceled) {
      setShowRenewPlanModal(true);
      return;
    }

    if (isCurrentPlan || +plan?.cost === 0) {
      setShowCancelPlanModal(true);
      return;
    }

    if (+plan?.cost >= +active_plan?.cost) {
      setShowChangePlanModal(true);
      return;
    }

    setShowDowngradePlanModal(true);
  };

  useEffect(() => {
    if (!setSelectedPlan && billingInfo) {
      setSelectedPlan(active_plan);
    }
  }, [billingInfo, setSelectedPlan]);

  return {
    showRenewPlanModal,
    setShowRenewPlanModal,
    showCancelPlanModal,
    setShowCancelPlanModal,
    showDowngradePlanModal,
    setShowDowngradePlanModal,
    showChangePlanModal,
    setShowChangePlanModal,
    setSelectedPlan,
    selectedPlan,
    plans,
    loading,
    frequency,
    setFrequency,
    onChangePlanIntent,
    onChangePlanSubmit,
  };
};

export default usePlans;
